/**
 * Standard tool result shape used by all plugin tool functions.
 *
 * Mirrors the MCP `CallToolResult` content structure so results can be
 * forwarded directly to either adapter without transformation.
 *
 * Construct via {@link ok}, {@link okJson}, or {@link err} — never manually.
 *
 * @docLink packages/workspace-plugin/concepts#plugin-types
 */
export interface ToolResult {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
}

/**
 * Return a successful plain-text {@link ToolResult}.
 *
 * @param text - Human-readable result string delivered to the agent.
 * @returns A `ToolResult` with `isError` unset.
 *
 * @docLink packages/workspace-plugin/concepts#plugin-types
 */
export function ok(text: string): ToolResult {
  return { content: [{ type: "text", text }] };
}

/**
 * Return a successful JSON {@link ToolResult}.
 *
 * Serializes `data` with two-space indentation.  The agent receives the
 * pretty-printed JSON string as a text block.
 *
 * @param data - Any JSON-serialisable value.
 * @returns A `ToolResult` with `isError` unset.
 *
 * @docLink packages/workspace-plugin/concepts#plugin-types
 */
export function okJson(data: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(data, null, 2) }] };
}

/**
 * Return an error {@link ToolResult}.
 *
 * The message is prefixed with `"Error: "` so agents can identify failures
 * without inspecting `isError`.
 *
 * @param message - Short description of what went wrong.
 * @returns A `ToolResult` with `isError: true`.
 *
 * @docLink packages/workspace-plugin/concepts#plugin-types
 */
export function err(message: string): ToolResult {
  return { content: [{ type: "text", text: `Error: ${message}` }], isError: true };
}

/**
 * Minimal session state shape persisted to `.skaile/sessions/<runId>.json`.
 *
 * Intentionally a subset of `runner/src/session.ts` — this package must not
 * depend on `@skaile/workspaces/runner` to avoid a circular dependency.
 *
 * @docLink packages/workspace-plugin/concepts#plugin-types
 */
export interface SessionState {
  runId: string;
  flowId: string;
  projectDir: string;
  agentDir?: string;
  driver: string;
  provider?: string;
  model?: string;
  driverSessionId?: string;
  startedAt: string;
  updatedAt: string;
  label?: string;
  status?: "running" | "complete" | "failed" | "paused";
}
